import {
  Avatar,
  Button,
  Modal,
  Space,
  Tag,
  TextArea,
  Typography,
} from '@douyinfe/semi-ui';
import { useEffect } from 'react';
import { useEventBus } from '@byted/hooks';
import { ModalReactProps } from '@douyinfe/semi-ui/lib/es/modal';
import { SafeHtmlContent } from '../Safe';
import { ReplyOperation } from './ReplyOperation';

import {
  Comment,
  Reply,
} from '@/typings/open.platform.forum_bff/namespaces/forum';
import { useComment } from '@/context/useComment';
import { useComment as useCommentApi } from '@/hooks/useComment';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useReply } from '@/hooks/useReply';
import {
  useCommentSelfVisible,
  useReplySelfVisible,
} from '@/hooks/useSelfVisible';
import { Biz } from '@/typings/open.platform.forum_bff/namespaces/configure';
import useBiz from '@/context/useBiz';

type Props = {
  content: Reply | Comment;
};

function isReply(man: Reply | Comment): man is Reply {
  return man && 'CommentId' in man;
}
export const CommentItem = ({ content }: Props) => {
  const changeCommentId = useComment(state => state.changeCommentId);
  const { isAdmin, User } = useUserInfo();
  useEffect(() => {
    if (!isReply(content)) {
      changeCommentId(content.Id);
    }
  }, [content]);

  const { toggleDelete, deleteComment } = useCommentApi();
  const { deleteReply } = useReply();
  const { run: replySelfFetch } = useReplySelfVisible();
  const { run: commentSelfFetch } = useCommentSelfVisible();
  const { trigger: reloadReply } = useEventBus('RELOAD_REPLY');
  const { trigger: reloadComment } = useEventBus('RELOAD_COMMENT');
  const ifBelongToMe = content.UserId === User?.Id;
  const showAdminTitle = content.UserInfo?.Role === 'ADMINISTRATOR';
  const deleteFromAdmin =
    !isReply(content) && content.IsDeleteFromAdmin && content.Delete;
  const deleteFromUser =
    !isReply(content) && content.Delete && !content.IsDeleteFromAdmin;
  const biz = useBiz(state => state.biz);

  const handleDeleteFromUser = (_content: typeof content, _biz: Biz) => {
    const replyOkFn: ModalReactProps['onOk'] = async () => {
      await deleteReply({
        Uid: '',
        ReplyId: content.Id,
        Reason: 'self',
        Biz: _biz,
      });
      reloadReply((_content as Reply).CommentId);
    };
    const commentOkFn: ModalReactProps['onOk'] = async () => {
      await deleteComment({
        CommentId: content.Id,
        Reason: 'self',
        Uid: '',
        Biz: _biz,
      });
      reloadComment();
    };

    Modal.confirm({
      title: _content.Delete ? '恢复删除？' : '确认删除？',
      onOk: isReply(_content) ? replyOkFn : commentOkFn,
    });
  };
  const handleDeleteFromAdmin = (_content: typeof content, _biz: Biz) => {
    let reason = '';
    const replyOkFn: ModalReactProps['onOk'] = async () => {
      await deleteReply({
        Uid: '',
        ReplyId: content.Id,
        Reason: reason,
        Biz: _biz,
      });
      reloadReply((_content as Reply).CommentId);
    };
    const commentOkFn: ModalReactProps['onOk'] = async () => {
      await toggleDelete({
        CommentId: content.Id,
        Reason: reason,
        Uid: '',
        Biz: _biz,
      });
      reloadComment();
    };

    const modal = Modal.confirm({
      title: _content.Delete ? '恢复删除？' : '确认删除？',
      okButtonProps: {
        disabled: !reason,
      },
      content: (
        <TextArea
          maxLength={140}
          maxCount={140}
          showCounter={true}
          onChange={value => {
            reason = value;
            modal.update({
              okButtonProps: {
                disabled: !value,
              },
              type: 'confirm',
            });
          }}
        />
      ),
      onOk: isReply(_content) ? replyOkFn : commentOkFn,
    });
  };

  return (
    <div className="flex" id={content.Id}>
      <div>
        <Avatar src={content.UserInfo?.Avatar} className={'w-8 h-8'} />
      </div>
      <div className="ml-2">
        <Space>
          <div className="space-x-2">
            <Typography.Text strong={true}>
              {content.UserInfo?.Name}
            </Typography.Text>
            {showAdminTitle && (
              <Typography.Text>
                {<Tag color="orange">管理员</Tag>}
              </Typography.Text>
            )}

            {isReply(content) && (
              <>
                <Typography.Text>{<span>回复</span>}</Typography.Text>
                <Typography.Text strong={true}>
                  {content.ToUserInfo?.Name}
                </Typography.Text>
              </>
            )}
            <Typography.Text type="tertiary">
              {content.CreatedAt}
            </Typography.Text>
          </div>

          <Space>
            {isAdmin && (
              <>
                {deleteFromAdmin && <Tag>管理员删除</Tag>}
                {deleteFromUser && <Tag>用户删除</Tag>}
                {!deleteFromUser && (
                  <Button
                    theme="borderless"
                    type="danger"
                    onClick={() => {
                      handleDeleteFromAdmin(content, biz);
                    }}>
                    {!isReply(content) && content.Delete ? '恢复删除' : '删除'}
                  </Button>
                )}
              </>
            )}
            {ifBelongToMe && !isAdmin && (
              <Button
                theme="borderless"
                type="danger"
                onClick={() => {
                  handleDeleteFromUser(content, biz);
                }}>
                {'删除'}
              </Button>
            )}

            {isAdmin && !content.Delete && (
              <Tag
                color="red"
                className=" cursor-pointer"
                onClick={() => {
                  let reason = '';
                  if (isReply(content)) {
                    const modal1 = Modal.confirm({
                      title: content.Visible ? '自见' : '取消自见',
                      content: (
                        <TextArea
                          maxLength={140}
                          maxCount={140}
                          placeholder="请输入原因"
                          onChange={value => {
                            reason = value;
                            modal1.update({
                              okButtonProps: {
                                disabled: !value,
                              },
                              type: 'confirm',
                            });
                          }}
                        />
                      ),
                      okButtonProps: {
                        disabled: !reason,
                      },
                      async onOk() {
                        await replySelfFetch({
                          Visible: !content.Visible,
                          Uid: '',
                          Biz: biz,
                          ReplyId: content.Id,
                          Reason: reason,
                        });
                        reloadReply(content.CommentId);
                      },
                    });
                  } else {
                    const modal2 = Modal.confirm({
                      title: content.Visible ? '自见' : '取消自见',
                      content: (
                        <TextArea
                          maxCount={140}
                          maxLength={140}
                          placeholder="请输入原因"
                          onChange={value => {
                            reason = value;
                            modal2.update({
                              okButtonProps: {
                                disabled: !value,
                              },
                              type: 'confirm',
                            });
                          }}
                        />
                      ),
                      okButtonProps: {
                        disabled: !reason,
                      },
                      async onOk() {
                        await commentSelfFetch({
                          Visible: !content.Visible,
                          Uid: '',
                          Biz: biz,
                          CommentId: content.Id,
                          Reason: reason,
                        });
                        reloadComment();
                      },
                    });
                  }
                }}>
                {!content.Visible ? '取消自见' : '自见'}
              </Tag>
            )}
          </Space>
        </Space>
        <SafeHtmlContent className="my-1">{content.Html || ''}</SafeHtmlContent>

        {!content.Delete && <ReplyOperation comment={content} />}
      </div>
    </div>
  );
};
